import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../styles/RegisterPage.css';
import '../styles/AuthPages.css';

function RegisterPage() {
    const [searchParams] = useSearchParams();
    const selectedPlan = searchParams.get('plan') || 'basico';

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        plan: selectedPlan,
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const { register } = useAuth();
    const navigate = useNavigate();

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Validações
        if (!formData.name || !formData.email || !formData.password || !formData.confirmPassword) {
            setError('Por favor, preencha todos os campos');
            return;
        }

        if (formData.password.length < 6) {
            setError('A senha deve ter pelo menos 6 caracteres');
            return;
        }

        if (formData.password !== formData.confirmPassword) {
            setError('As senhas não coincidem');
            return;
        }

        try {
            setLoading(true);
            await register(formData.email, formData.password, formData.name, formData.plan);

            // Se o plano for pago, redirecionar para página de pagamento
            if (formData.plan === 'profissional' || formData.plan === 'premium') {
                navigate('/pagamento');
            } else {
                navigate('/dashboard');
            }
        } catch (err) {
            console.error('Erro ao registrar:', err);
            if (err.code === 'auth/email-already-in-use') {
                setError('Este email já está em uso');
            } else if (err.code === 'auth/invalid-email') {
                setError('Email inválido');
            } else if (err.code === 'auth/weak-password') {
                setError('Senha muito fraca');
            } else {
                setError('Erro ao criar conta. Tente novamente.');
            }
        } finally {
            setLoading(false);
        }
    };

    const plans = {
        basico: {
            name: 'Básico',
            price: 'Grátis',
            color: '#48bb78',
        },
        profissional: {
            name: 'Profissional',
            price: 'R$ 29,90/mês',
            color: '#667eea',
        },
        premium: {
            name: 'Premium',
            price: 'R$ 59,90/mês',
            color: '#764ba2',
        },
    };

    return (
        <div className="register-page">
            <div className="register-container">
                <div className="register-card">
                    <div className="register-header">
                        <h1>Criar Conta</h1>
                        <p>Comece a organizar seu negócio hoje mesmo</p>
                    </div>

                    {/* Plan Selected */}
                    <div className="selected-plan" style={{ borderColor: plans[formData.plan].color }}>
                        <div className="plan-info">
                            <span className="plan-label">Plano Selecionado:</span>
                            <span className="plan-name" style={{ color: plans[formData.plan].color }}>
                                {plans[formData.plan].name}
                            </span>
                            <span className="plan-price">{plans[formData.plan].price}</span>
                        </div>
                        <Link to="/planos" className="change-plan-link">
                            Mudar plano
                        </Link>
                    </div>

                    {error && (
                        <div className="alert alert-danger">
                            <i className="bi bi-exclamation-circle"></i> {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="register-form">
                        <div className="form-group">
                            <label htmlFor="name">Nome Completo</label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                className="form-control"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Seu nome"
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="email">Email</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                className="form-control"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="seu@email.com"
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="password">Senha</label>
                            <input
                                type="password"
                                id="password"
                                name="password"
                                className="form-control"
                                value={formData.password}
                                onChange={handleChange}
                                placeholder="Mínimo 6 caracteres"
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="confirmPassword">Confirmar Senha</label>
                            <input
                                type="password"
                                id="confirmPassword"
                                name="confirmPassword"
                                className="form-control"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                placeholder="Digite a senha novamente"
                                disabled={loading}
                            />
                        </div>

                        <button
                            type="submit"
                            className="btn btn-primary btn-block"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                    Criando conta...
                                </>
                            ) : (
                                'Criar Conta'
                            )}
                        </button>
                    </form>

                    <div className="register-footer">
                        <p>
                            Já tem uma conta? <Link to="/login">Fazer login</Link>
                        </p>
                    </div>

                    <div className="back-home-link">
                        <Link to="/">
                            <i className="bi bi-arrow-left"></i> Voltar para página inicial
                        </Link>
                    </div>
                </div>

                {/* Info Side */}
                <div className="register-info">
                    <h2>Por que escolher o MicroGestor?</h2>
                    <div className="benefits-list">
                        <div className="benefit-item">
                            <i className="bi bi-check-circle-fill"></i>
                            <div>
                                <h3>Configuração Rápida</h3>
                                <p>Comece a usar em menos de 5 minutos</p>
                            </div>
                        </div>
                        <div className="benefit-item">
                            <i className="bi bi-check-circle-fill"></i>
                            <div>
                                <h3>Dados Seguros</h3>
                                <p>Seus dados protegidos com criptografia</p>
                            </div>
                        </div>
                        <div className="benefit-item">
                            <i className="bi bi-check-circle-fill"></i>
                            <div>
                                <h3>Suporte Dedicado</h3>
                                <p>Estamos aqui para ajudar você</p>
                            </div>
                        </div>
                        <div className="benefit-item">
                            <i className="bi bi-check-circle-fill"></i>
                            <div>
                                <h3>Sem Compromisso</h3>
                                <p>Cancele quando quiser, sem taxas</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default RegisterPage;
